
import React, { Component, useCallback, useState } from 'react'
import { observable, action, runInAction, computed } from 'mobx'
import { observer } from 'mobx-react'

import { Select, SelectProps } from '@instructure/ui-select'
import { Spinner } from '@instructure/ui-spinner'
import { TagProps } from '@instructure/ui-tag'
import { Tag } from '@instructure/ui-tag'
import { Text } from '@instructure/ui-text'

import { makeInstUIAdapter } from '../final_form/FormAdapters'
import { observerMethod } from '@matchlighter/common_library/lib/decorators/method_component'
import { toTitleCase } from '@matchlighter/common_library/lib/strings'

export interface OptionMeta {
    id: any;
    disabled: boolean;
    selected: boolean;
    highlighted: boolean;
}

interface NormalizedCacheOptions {
    key: string,
    timeout?: number,
}

type CloseReason = 'selection' | 'blurred' | 'other';

interface AsyncSelectProps_Common<D> {
    icon?: any;

    SelectComponent?;

    options: D[] | ((term: string) => D[] | PromiseLike<D[]>);
    should_cache_options?: false | ((term: string, options: D[]) => boolean | number | string | NormalizedCacheOptions);
    prefetch?: boolean;
    onTermChanged?: (term: string) => void;
    selectById?: boolean;
    getOptionId?: (opt: D) => string;

    onDropdownShown?: (initialValue: string) => void;
    onDropdownHidden?: (enteredValue: string, filteredValues: D[]) => void;
    overrideOnClose?: (reason: CloseReason, event) => boolean;

    minSearchLength?: number;
    renderOption?: (opt: D, meta: OptionMeta) => React.ReactChild;
    renderNullOption?: boolean | (() => React.ReactChild);
    getOptionLabel?: (opt: D | null) => string;

    searchable?: boolean;
}

type AsyncSelectProps<D> = SelectProps & AsyncSelectProps_Common<D>

type SelectOptionType = { id: any, _icon?: any; _value?: any; } | string | number

interface AsyncSelectProps_Base<D> extends AsyncSelectProps<D> {
    isOptionSelected: (opt: D) => boolean;
    onOptionSelected: (opt: D) => void;

    initialEditValue?: string;
    initialSearchValue?: string;
    excludeSelected?: boolean;

    onClearLastItem?: () => void;
}

interface AsyncSelectProps_Single<D> extends AsyncSelectProps<D> {
    onOptionSelected: (opt: D) => void;
    selectedOption: D;
    includeAll?: boolean;
    isNewItem?: boolean;
}

export type RenderSelectedOptionTag<D> = (opt: D, meta: { index: number, props: TagProps & { key: string } }) => React.ReactChild;

interface AsyncSelectProps_Multiple<D> extends AsyncSelectProps<D> {
    selectedOptions: D[];
    renderSelectedOptionTag?: RenderSelectedOptionTag<D>;
    onValueChanged: (opt: D[]) => void;
}

function getOptionLabel<D>(opt: D, props: AsyncSelectProps<D>) {
    if (props.getOptionLabel) return props.getOptionLabel(opt);
    if (!opt) return null;
    if (typeof opt == 'string') return toTitleCase(opt);
    return (opt as any).label || (opt as any).name || toTitleCase((opt as any).id.toString());
}

function getOptionId(opt: SelectOptionType, props: AsyncSelectProps_Common<any>) {
    if (typeof opt == 'string') return opt;
    if (props.getOptionId) return props.getOptionId(opt);

    if (typeof opt == 'object') {
        return opt.id;
    }
    return opt
}

function mapToOption(optKey, allOptions, props: AsyncSelectProps_Common<any>) {
    if (!optKey) return null;
    if (typeof optKey == 'object') return optKey;

    if (typeof allOptions != 'function') {
        for (let opt of allOptions) {
            if (getOptionId(opt, props) == getOptionId(optKey, props)) return opt
        }
    }

    return optKey;
}

export class FetchUserError extends Error { }

@observer
class AsyncSelectBase<D extends SelectOptionType> extends Component<AsyncSelectProps_Base<D>> {
    @observable inputValue: string = '';
    @observable searchValue: string;
    @observable isShowingOptions = false;
    @observable highlightedOptionId = null;
    @observable announcementString: string = null;

    @observable lastError: Error;
    @observable.struct filteredOptions: D[] = [];

    static defaultProps: Partial<AsyncSelectProps_Base<any>> = {
        SelectComponent: Select,
        should_cache_options: (key, result) => key == '',
    }

    @computed
    get isLoading() { return this.nextSearchToken > this.currentSearchToken || this.props.options == undefined }

    @computed
    get isSearchable() {
        if (this.props.searchable != null) return this.props.searchable;
        return (typeof this.props.options == 'function') || this.props.onTermChanged != null;
    }

    @computed
    get showNullOption() {
        if (this.props.renderNullOption != undefined) {
            return !!this.props.renderNullOption;
        }
        return false;
    }

    @computed
    get minSearchLength() {
        if (!this.isSearchable) return 0;
        if (this.props.minSearchLength == null) {
            if (typeof this.props.options == 'function') return 3;
            return 0;
        }
        return this.props.minSearchLength;
    }

    @computed
    get inputDisplayText() {
        return (this.isShowingOptions ? this.inputValue : this.props.initialEditValue) || ''
    }

    getOptionById(queryId) {
        return this.filteredOptions.find((item) => getOptionId(item, this.props) === queryId)
    }

    componentDidMount() {
        if (this.props.prefetch) {
            this.fetchFilteredResults();
        }
    }

    componentDidUpdate(prevProps: AsyncSelectProps_Base<D>) {
        if (this.isShowingOptions) {
            if (prevProps.options != this.props.options) {
                this.fetchFilteredResults();
            }
        }
    }

    @action.bound
    handleShowOptions(event) {
        this.isShowingOptions = true;
        this.inputValue = this.props.initialEditValue || '';

        if (this.props.initialSearchValue != null && this.props.initialSearchValue.length >= this.minSearchLength) {
            this.searchValue = this.props.initialSearchValue;
        } else {
            this.searchValue = this.inputValue;
        }

        if (!this.props.prefetch) {
            this.filteredOptions = []
            this.fetchFilteredResults();
        }

        if (this.props.onDropdownShown) this.props.onDropdownShown(this.inputValue);
    }

    @action.bound
    handleHideOptions(event) {
        if (this.shouldCloseOnEvent(event)) {
            this.isShowingOptions = false;
            this.highlightedOptionId = null;
            this.announcementString = "List collapsed."

            if (this.props.onDropdownHidden) {
                this.props.onDropdownHidden(this.inputValue, this.filteredOptions);
            }
        }
    }

    @action.bound
    handleBlur(event) {
        this.highlightedOptionId = null;
        this.props.onBlur && this.props.onBlur(event);
    }

    private shouldCloseOnEvent(event, reason?: CloseReason) {
        if (!reason) {
            reason = event.nativeEvent instanceof FocusEvent ? 'blurred' : 'other';
        }
        return this.props.overrideOnClose ? this.props.overrideOnClose(reason, event) !== false : true;
    }

    @action.bound
    handleHighlightOption(event, { id }) {
        if (id == 'null-option') {
            this.announcementString = 'Clear entry';
            this.inputValue = event.type === 'keydown' ? '' : this.inputValue;
        } else {
            const option = this.getOptionById(id)
            if (!option) return // prevent highlighting of empty option

            const label = getOptionLabel(option, this.props);
            this.inputValue = event.type === 'keydown' ? label : this.inputValue;
            this.announcementString = label;
        }
        this.highlightedOptionId = id;
    }

    @action.bound
    handleSelectOption(event, { id }) {
        if (id === 'null-option') {
            this.props.onOptionSelected(null);
            this.inputValue = '';
            this.announcementString = `Value cleared. List collapsed.`;
        } else {
            const option = this.getOptionById(id)
            if (!option) return // prevent selecting of empty option
            this.props.onOptionSelected(this.props.selectById ? id : option);

            const label = getOptionLabel(option, this.props);
            this.inputValue = label;
            this.announcementString = `${label} selected. List collapsed.`;
        }
        if (this.shouldCloseOnEvent(event, 'selection')) {
            this.isShowingOptions = false;
        }
    }

    private optionResultsCache = {};
    private async getAsyncOptions(term: string) {
        if (typeof this.props.options != 'function') return [];

        const preCacheProps = this.getCacheParams(term);
        if (preCacheProps && this.optionResultsCache[preCacheProps.key]) {
            return this.optionResultsCache[preCacheProps.key];
        }

        term = term || '';
        const options = await this.props.options(term);

        // TODO Be smarter and implement some sort of expiry and limit
        const cacheProps = this.getCacheParams(term, options);
        if (cacheProps) {
            this.optionResultsCache[cacheProps.key] = options;
        }

        return options;
    }

    private getCacheParams(searchTerm: string, options?): NormalizedCacheOptions {
        if (!this.props.should_cache_options) return null;

        const cacheOpt = this.props.should_cache_options(searchTerm, options);
        if (typeof cacheOpt == 'number') {
            if (cacheOpt < 1) return null;
            return { key: searchTerm, timeout: cacheOpt }
        } else if (typeof cacheOpt == 'string') {
            return { key: cacheOpt }
        } else if (typeof cacheOpt == 'boolean') {
            return { key: searchTerm }
        }
        return cacheOpt;
    }

    @observable private nextSearchToken = 0;
    @observable private currentSearchToken = 0;
    private async fetchFilteredResults() {
        if (this.minSearchLength && this.searchValue.length < this.minSearchLength) {
            runInAction(() => {
                this.filteredOptions = [];
            })
            return;
        }

        let searchToken;
        runInAction(() => {
            // this.isShowingOptions = true
            this.lastError = null;
            searchToken = ++this.nextSearchToken;
        });

        let filteredOptions: D[];

        try {
            if (typeof this.props.options == 'function') {
                filteredOptions = await this.getAsyncOptions(this.searchValue);
            } else {
                filteredOptions = this.props.options;
                if (this.isSearchable && !this.props.onTermChanged) {
                    filteredOptions = filteredOptions.filter((v, i) => {
                        const l = getOptionLabel(v, this.props);
                        return !l || (l as string).indexOf(this.searchValue) >= 0;
                    });
                }
            }
        } catch (err) {
            runInAction(() => {
                if (searchToken >= this.currentSearchToken) {
                    this.currentSearchToken = searchToken;
                };
                this.lastError = err;
            })
            return;
        }

        if (searchToken < this.currentSearchToken) return;

        runInAction(() => {
            this.filteredOptions = filteredOptions || [];
            this.currentSearchToken = searchToken;
            this.lastError = null;
        })
    }

    @computed.struct
    get internalFilteredOptions() {
        let opts = this.filteredOptions;
        if (this.props.excludeSelected) {
            opts = opts.filter(opt => !this.props.isOptionSelected(opt))
        }
        return opts;
    }

    @action.bound
    handleInputChange(event) {
        const value = event.target.value;
        this.inputValue = value;
        this.searchValue = value;
        this.isShowingOptions = true;
        this.highlightedOptionId = null;

        if (this.props.onTermChanged) {
            this.props.onTermChanged(value);
        }

        this.fetchFilteredResults();

        // announcement: 'Loading options.'
        // announcement: `${newOptions.length} options available.`
    }

    @action.bound
    handleKeyDown(event) {
        if (event.keyCode === 8) {
            // when backspace key is pressed
            if (this.inputValue === '' && this.props.onClearLastItem) {
                // remove last selected option, if input has no entered text
                this.props.onClearLastItem();
            } else if (!this.isSearchable) {
                // this.props.onOptionSelected(null);
            }
        }
    }

    renderOption(option: D) {
        const isSelected = this.props.isOptionSelected(option);
        const optId = getOptionId(option, this.props);

        let optionContent;
        if (this.props.renderOption) {
            optionContent = this.props.renderOption(option, {
                id: optId,
                disabled: (option as any).disabled,
                selected: isSelected,
                highlighted: optId === this.highlightedOptionId,
            })
        } else {
            optionContent = getOptionLabel(option, this.props);
        }

        return <Select.Option
            id={optId}
            key={optId}
            isHighlighted={optId === this.highlightedOptionId}
            isSelected={isSelected}
            isDisabled={(option as any).disabled}
            renderBeforeLabel={(option as any)._icon}
        >
            {optionContent}
        </Select.Option>
    }

    renderErrorOption() {
        if (!this.lastError || this.isLoading) return null;

        let message: string;
        if (typeof this.lastError == 'string') {
            message = this.lastError;
        } else if (this.lastError instanceof FetchUserError) {
            message = this.lastError.message;
        } else {
            message = 'An error occurred while fetching results'
        }

        return <Select.Option id="error-option" key="error-option">
            <Text color="secondary" fontStyle="italic">{message}</Text>
        </Select.Option>
    }

    renderLoadingOption() {
        if (!this.isLoading) return null;
        return <Select.Option id="loading-option" key="loading-option">
            <Spinner renderTitle="Loading" size="x-small" />
        </Select.Option>
    }

    renderEmptyOption() {
        if (this.isLoading || this.lastError) return null;

        let content;

        if ((!this.inputValue || this.inputValue.length < this.minSearchLength) && this.isSearchable) {
            if (this.minSearchLength > 1) {
                content = `Type at least ${this.minSearchLength} characters to search`
            } else {
                content = 'Type to search'
            }
        } else if (this.isSearchable) {
            content = 'No results';
        } else {
            content = 'No items'
        }

        return <Select.Option id="empty-option" key="empty-option">
            {content}
        </Select.Option>
    }

    renderNullOption() {
        if (!this.showNullOption) return null;
        const opt_id = 'null-option';

        let content;
        if (typeof this.props.renderNullOption == 'function') {
            content = this.props.renderNullOption();
        } else {
            content = '---'
        }

        return <Select.Option
            id={opt_id}
            key={opt_id}
            isHighlighted={opt_id === this.highlightedOptionId}
        >
            {content}
        </Select.Option>
    }

    renderOptions() {
        return this.internalFilteredOptions.map((option) => {
            return this.renderOption(option)
        })
    }

    render() {
        const {
            icon, options, renderOption, getOptionLabel,
            searchable, isOptionSelected, onOptionSelected, initialEditValue,
            SelectComponent: Select,
            ...passProps
        } = this.props;

        return (
            <>
                <Select
                    assistiveText="Type to search"

                    {...passProps}

                    inputValue={this.inputDisplayText}
                    isShowingOptions={this.isShowingOptions}

                    onBlur={this.handleBlur}
                    onInputChange={this.isSearchable ? this.handleInputChange : null}
                    onRequestShowOptions={this.handleShowOptions}
                    onRequestHideOptions={this.handleHideOptions}
                    onRequestHighlightOption={this.handleHighlightOption}
                    onRequestSelectOption={this.handleSelectOption}

                    // @ts-ignore
                    onKeyDown={this.handleKeyDown}

                    renderAfterInput={this.props.renderAfterInput || icon}
                >
                    {this.renderErrorOption()}
                    {this.renderLoadingOption()}
                    {this.renderNullOption()}
                    {(this.internalFilteredOptions.length > 0) ? this.renderOptions() : this.renderEmptyOption()}
                </Select>
                {/* <Alert
                    liveRegion={() => document.getElementById('flash-messages')}
                    liveRegionPoliteness="assertive"
                    screenReaderOnly
                >
                    {this.annoucementString}
                </Alert> */}
            </>
        )
    }
}

@observer
export class AsyncSelect<D extends SelectOptionType> extends Component<AsyncSelectProps_Single<D>> {

    @computed
    get selectedOption() {
        return mapToOption(this.props.selectedOption, this.props.options, this.props);
    }

    private determineBestOptionOnClose(value: string, filteredValues: D[]): D {
        if (value.length == 0) return null;
        // an option matching user input exists
        if (filteredValues.length === 1) {
            const onlyOption = filteredValues[0]
            // automatically select the matching option
            // if (this.getOptionLabel(onlyOption).toLowerCase() === this.inputValue.toLowerCase()) {
            //     return onlyOption
            // }
            return onlyOption;
        }
    }

    @action.bound
    handleDropdownHidden(value: string, filteredValues: D[]) {
        const bestOption = this.determineBestOptionOnClose(value, filteredValues);
        if (bestOption !== undefined) this.props.onOptionSelected(bestOption);
    }

    isOptionSelected = (opt: D) => {
        return this.props.selectedOption && getOptionId(opt, this.props) == getOptionId(this.props.selectedOption, this.props);
    }

    render() {
        const { selectedOption, ...pass } = this.props;
        const selOpt: any = this.selectedOption;

        return <AsyncSelectBase
            renderBeforeInput={selOpt && selOpt._icon || undefined}
            initialSearchValue={''}

            {...pass}

            isOptionSelected={this.isOptionSelected}
            initialEditValue={getOptionLabel(this.selectedOption, this.props) || ''}

            onDropdownHidden={this.handleDropdownHidden}
        />
    }
}

@observer
export class AsyncMultiSelect<D extends SelectOptionType> extends Component<AsyncSelectProps_Multiple<D>> {
    @observable initialEditValue = '';

    @action.bound
    handleOptionSelected(opt: D) {
        if (!this.props.selectedOptions.includes(opt)) {
            this.props.onValueChanged([...this.props.selectedOptions, opt])
        }
    }

    @action.bound
    handleDropdownHidden(value: string, filteredValues: D[]) {
        this.initialEditValue = value;
    }

    @action.bound
    removeLastItem() {
        if (this.props.selectedOptions.length > 0) {
            this.props.onValueChanged(this.props.selectedOptions.slice(0, -1));
        }
    }

    @action.bound
    removeItem(event, opt) {
        event.stopPropagation()
        event.preventDefault()

        const selOpts = this.props.selectedOptions;
        if (selOpts.includes(opt)) {
            this.props.onValueChanged(selOpts.filter(so => so != opt))
        }
    }

    isOptionSelected = (opt: D) => {
        const selectedIds = (this.props.selectedOptions || []).map(opt => getOptionId(opt, this.props))
        return selectedIds.includes(getOptionId(opt, this.props))
    }

    @observerMethod
    renderBeforeInput() {
        const props = this.props;

        let renderedBeforeInput = null;
        if (props.renderBeforeInput) {
            const rbi = props.renderBeforeInput;
            renderedBeforeInput = (typeof rbi == 'function') ? rbi() : rbi;
        }

        const renderSelectedTag = props.renderSelectedOptionTag || (!props.renderBeforeInput && ((opt, meta) => {
            return <Tag {...meta.props} />
        }))

        let renderedOptions = null;
        if (renderSelectedTag) {
            renderedOptions = this.props.selectedOptions.map((opt, index) => {
                const sourceOption = mapToOption(opt, this.props.options, this.props);
                const resolvedOption = sourceOption || opt;

                const label = getOptionLabel(resolvedOption, this.props);
                return renderSelectedTag(resolvedOption, {
                    index,
                    props: {
                        dismissible: true,
                        key: getOptionId(resolvedOption, this.props),
                        title: `Remove ${label}`,
                        text: label,
                        margin: index > 0 ? 'xxx-small 0 xxx-small xx-small' : 'xxx-small 0',
                        onClick: (e) => this.removeItem(e, opt),
                    },
                });
            })
        }

        return <>
            {renderedBeforeInput}
            {renderedOptions}
        </>
    }

    getRenderBeforeInput() {
        if (this.props.selectedOptions.length > 0) {
            return this.renderBeforeInput;
        }
        return this.props.renderBeforeInput
    }

    render() {
        const { onValueChanged, selectedOptions, renderSelectedOptionTag, ...pass } = this.props;
        return <AsyncSelectBase
            {...pass}

            excludeSelected
            // initialEditValue={this.initialEditValue}
            isOptionSelected={this.isOptionSelected}
            onOptionSelected={this.handleOptionSelected}
            onDropdownHidden={this.handleDropdownHidden}
            onClearLastItem={this.removeLastItem}

            renderBeforeInput={this.getRenderBeforeInput()}
        />
    }
}

export const AsyncSelectAdapter = makeInstUIAdapter(AsyncSelect, x => x, {
    changeKey: 'onOptionSelected',
    valueKey: 'selectedOption',
})

export const AsyncMultiSelectAdapter = makeInstUIAdapter(AsyncMultiSelect, x => x, {
    changeKey: 'onValueChanged',
    valueKey: 'selectedOptions',
})

type OrMulti<D> = {
    multi?: true,
    value: D[],
    onValueChanged: (opt: D[]) => void;
} | {
    multi?: false,
    value: D,
    onValueChanged: (opt: D) => void;
}

export type AsyncSelect2Props<D> = AsyncSelectProps<D> & {
    renderSelectedOptionTag?: RenderSelectedOptionTag<D>;
} & OrMulti<D>

export type WAsyncSelect2Props<D> = Omit<AsyncSelectProps<D>, 'options'> & OrMulti<D>

export const AsyncSelect2 = <D extends any>(props: AsyncSelect2Props<D>) => {
    const { multi, value, onValueChanged, renderSelectedOptionTag, ...rest } = props;

    const [valueState, setValue] = useState(multi ? [] : null);
    const valueChanged = useCallback((nvalue) => {
        if (onValueChanged) onValueChanged(nvalue);
        if (value == undefined) setValue(nvalue);
    }, [value, onValueChanged])

    const currentValue = value === undefined ? valueState : value;

    if (props.multi) {
        // @ts-ignore
        return <AsyncMultiSelect
            selectedOptions={currentValue as D[]}
            onValueChanged={valueChanged}
            renderSelectedOptionTag={renderSelectedOptionTag}
            {...rest}
        />
    } else {
        return <AsyncSelect
            selectedOption={currentValue as any}
            onOptionSelected={valueChanged as any}
            {...rest}
        />
    }
}

export const AsyncSelect2Adapter = makeInstUIAdapter(AsyncSelect2, x => x, {
    changeKey: 'onValueChanged',
    valueKey: 'value',
})
